/**
 * "จด" sheet: one-tap stimulus tags + optional short note.
 * Opened from a button next to the belly, so tapping kicks is never slowed down.
 */
import { makeEntry, NOTE_MAX, TAGS, type JournalEntry } from '../domain/journal';
import { repo } from '../data/repo';
import { newId } from '../data/localStore';
import { fmtDate, fmtTime, t } from '../i18n';
import { sfx } from '../audio/sfx';
import { closeSheet, esc, openSheet, toast, vibrate } from './dom';
import { momentsRowHtml, openMoment } from './momentSheet';
import type { MomentKind } from '../domain/moments';

const tagEmoji = (id: string) => TAGS.find(x => x.id === id)?.emoji ?? '•';
const tagLabel = (id: string) => (TAGS.some(x => x.id === id) ? t('tags', id as (typeof TAGS)[number]['id']) : id);

function recentHtml(): string {
  const items = [...repo.journal].sort((a, b) => b.occurredAt - a.occurredAt).slice(0, 5);
  if (!items.length) return `<p class="small muted" style="text-align:center">${esc(t('journal', 'empty'))}</p>`;
  return items.map((e: JournalEntry) => `
    <div class="jentry">
      <div class="jentry-main">
        <div class="jentry-when">${esc(fmtDate(e.occurredAt))} · ${esc(fmtTime(e.occurredAt))}</div>
        ${e.tags.length ? `<div class="jentry-tags">${e.tags.map(id => `<span class="pill">${tagEmoji(id)} ${esc(tagLabel(id))}</span>`).join('')}</div>` : ''}
        ${e.note ? `<div class="jentry-note">${esc(e.note)}</div>` : ''}
      </div>
      <button class="icon-btn jdel" data-action="jdel" data-id="${esc(e.clientId)}" aria-label="${esc(t('journal', 'delete'))}">×</button>
    </div>`).join('');
}

export function openJournal(): void {
  const picked = new Set<string>();
  openSheet(`
    <h3 class="jrecent-title" style="margin-top:0">${esc(t('moments', 'section'))}</h3>
    ${momentsRowHtml()}
    <h2 style="margin-top:18px">${esc(t('journal', 'title'))}</h2>
    <p class="muted" style="margin:4px 0 14px">${esc(t('journal', 'lead'))}</p>
    <div class="tag-grid">
      ${TAGS.map(tag => `<button class="chip tag-chip" data-action="jtag" data-tag="${tag.id}" aria-pressed="false"><span class="tag-emoji">${tag.emoji}</span>${esc(t('tags', tag.id))}</button>`).join('')}
    </div>
    <div class="field" style="margin-top:14px">
      <label for="j-note">${esc(t('journal', 'noteLabel'))}</label>
      <textarea id="j-note" class="note-input" rows="2" maxlength="${NOTE_MAX}" placeholder="${esc(t('journal', 'notePh'))}"></textarea>
    </div>
    <button class="btn btn-primary btn-block" data-action="jsave" id="j-save" disabled style="margin-top:12px">${esc(t('journal', 'save'))}</button>
    <h3 class="jrecent-title">${esc(t('journal', 'recent'))}</h3>
    <div id="j-recent">${recentHtml()}</div>
  `, {
    'open-moment': el => {
      closeSheet();
      setTimeout(() => openMoment(el.dataset.kind as MomentKind), 230);
    },
    jtag: el => {
      const id = el.dataset.tag!;
      if (picked.has(id)) picked.delete(id); else picked.add(id);
      el.classList.toggle('on', picked.has(id));
      el.setAttribute('aria-pressed', String(picked.has(id)));
      sfx.kick(1); vibrate(10);
      update();
    },
    jsave: () => {
      const note = (document.getElementById('j-note') as HTMLTextAreaElement).value;
      const entry = makeEntry(newId(), Date.now(), [...picked], note);
      if (!entry) return;
      repo.addJournal(entry);
      vibrate([20, 40, 20]);
      closeSheet();
      document.getElementById('jot-bubble')?.setAttribute('hidden', '');
      if (!repo.ui.jotLearned) { repo.setUi({ jotLearned: true }); toast(t('journal', 'firstSaved')); }
      else toast(t('journal', 'saved'));
    },
    jdel: el => {
      repo.removeJournal(el.dataset.id!);
      const box = document.getElementById('j-recent');
      if (box) box.innerHTML = recentHtml();
      toast(t('journal', 'deleted'));
    }
  }, { className: 'journal-sheet' });

  const note = document.getElementById('j-note') as HTMLTextAreaElement;
  const save = document.getElementById('j-save') as HTMLButtonElement;
  function update() { save.disabled = picked.size === 0 && note.value.trim() === ''; }
  note.addEventListener('input', update);
}
